import { Observer, observer } from "mobx-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { PlayerOptions } from "components/SoundClipEditor/SoundClipEditor";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { AcxTranscriptionStore } from "../Stores/AcxTranscriptionStore";
import { useWindowResize } from "../utils";
import { SpeakerStore } from "../Stores/SpeakerStore/SpeakerStore";
import copy from "copy-to-clipboard";
import AcxClipPopover from "./AcxClipPopover";
import TranscriptionRow, { TranscriptionRowData } from "./TranscriptionRow";
import { RedactDialog } from "./RedactDialog";
import type { ManualTranscriptRedactionType } from "services/TranscriptionService";

type Props = {
    playerBlurb?: (playerOptions: PlayerOptions) => void;
    audioClip?: { status: "default" | "success" | "error" | "loading" };
    hasEvaluation: boolean;
    onClip?: (start: number, end: number) => void;
    speakerStore?: SpeakerStore;
    disableClip?: boolean;
    disableRedact?: boolean;
    onRedactSelection?: (
        redactionType: ManualTranscriptRedactionType,
        selectedChannel?: number,
        selectedText?: string,
        selectedStartTime?: number,
        selectedEndTime?: number,
    ) => Promise<void>;
};

export const AcxTranscriptionBody: React.FC<Props> = observer(
    ({
        playerBlurb,
        audioClip,
        hasEvaluation,
        onClip,
        speakerStore,
        disableClip,
        disableRedact,
        onRedactSelection,
    }) => {
        const authstore = useStore(AuthStore);
        const store = useStore(AcxTranscriptionStore);
        const orgStore = authstore.orgStore;

        const selectedOrganizationId = orgStore.selectedOrganization?.id;

        useEffect(() => {
            if (selectedOrganizationId && !orgStore.hasLoadedRedaction) {
                void orgStore.loadRedactionEnabled(selectedOrganizationId);
            }
        }, [orgStore, orgStore.hasLoadedRedaction, selectedOrganizationId]);

        const [redactDialogOpen, setRedactDialogOpen] = useState(false);
        // Normal redactions must stay within one blurb. Multi-speaker selections
        // are allowed because the dialog lets the user pick one speaker to redact.
        const hasValidRedactionSelection =
            typeof store.selectedStartTimestamp === "number" &&
            typeof store.selectedEndTimestamp === "number" &&
            Number.isFinite(store.selectedStartTimestamp) &&
            Number.isFinite(store.selectedEndTimestamp) &&
            store.selectedEndTimestamp > store.selectedStartTimestamp &&
            (!!store.selectedBlurb ||
                store.overlappingSpeakers ||
                store.selectedHasMultipleChannels) &&
            !!store.selectedText?.trim();
        const canRedact =
            orgStore.hasRedaction &&
            authstore.canUserEdit("Redact from Transcript") &&
            !disableRedact &&
            !!onRedactSelection &&
            hasValidRedactionSelection;

        const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(null);
        const listRef = useRef<List>(null);
        const rows = store.transcriptionDisplayRows;

        // Capture scroll intent from store
        useEffect(() => {
            if (store.scrollIndex === null) return;
            setPendingScrollIndex(store.scrollIndex);
            store.setScrollIndex(null);
        }, [store, store.scrollIndex]);

        // Execute scroll once focused and list is rendered
        useEffect(() => {
            if (!store.focused || pendingScrollIndex === null) return;
            const timer = setTimeout(() => {
                // First scroll brings target items into view using estimated sizes.
                // Two rAFs: first waits for render+paint, second waits for useEffect
                // in TranscriptionRow (setSize → resetAfterIndex) to propagate,
                // then re-scrolls with accurate sizes.
                listRef.current?.scrollToItem(pendingScrollIndex, "start");
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        listRef.current?.scrollToItem(pendingScrollIndex, "start");
                    });
                });
                setPendingScrollIndex(null);
            }, 100);
            return () => clearTimeout(timer);
        }, [store.focused, pendingScrollIndex]);

        // Sizing for the list items and container
        const sizeMap = useRef({});
        const setSize = useCallback((index, size) => {
            sizeMap.current = { ...sizeMap.current, [index]: size };
            // @ts-ignore
            listRef.current?.resetAfterIndex(index);
        }, []);
        const getSize = (index) => sizeMap.current[index] || 80;

        const [windowWidth] = useWindowResize();

        // Add a useEffect eventlistener to override ctrl + c to copy the text
        useEffect(() => {
            const handleCopy = async (e: ClipboardEvent) => {
                e.preventDefault();
                if (!store.selectedText || store.selectedText.trim() === "") {
                    // If no text selected in the store, use the default copy behavior with window.getSelection()
                    const selection = window.getSelection();
                    if (selection && selection.toString().trim() !== "") {
                        const textToCopy = selection.toString();
                        await copy(textToCopy);
                    }
                    return;
                }
                const textToCopy = store.selectedText;
                await copy(textToCopy);
                store.setSelectedStartTimestamp(null);
                store.setSelectedEndTimestamp(null);
                store.setSelectedEndTimestampTemp(null);
            };
            document.addEventListener("copy", handleCopy);
            return () => {
                document.removeEventListener("copy", handleCopy);
            };
        }, [store, store.selectedText]);

        if (!authstore.canUserView("Transcription")) {
            return null;
        }


        return (
            <AutoSizer className="pendo-ignore">
                {({ height, width }) => (
                    <Observer>
                        {() => (
                            <div
                                id="transcriptionContainer"
                                style={{ width: "100%", height: "100%" }}
                            >
                                <AcxClipPopover
                                    store={store}
                                    onCreateClip={async () => {
                                        if (
                                            !store.selectedStartTimestamp ||
                                            !store.selectedEndTimestamp ||
                                            !onClip
                                        )
                                            return;
                                        await onClip(
                                            store.selectedStartTimestamp,
                                            store.selectedEndTimestamp,
                                        );
                                    }}
                                    onRedact={() => {
                                        store.saveLastSelection();
                                        setRedactDialogOpen(true);
                                    }}
                                    disableClip={disableClip}
                                    disableRedact={!canRedact}
                                />
                                <RedactDialog
                                    open={redactDialogOpen}
                                    setOpen={setRedactDialogOpen}
                                    speakerStore={speakerStore}
                                    onRedactSelection={onRedactSelection}
                                />
                                <List
                                    ref={listRef}
                                    height={height - 1}

                                    itemCount={
                                        rows.length
                                    }
                                    width={width - 1}
                                    itemData={
                                        {
                                            rows,
                                            setSize,
                                            windowWidth,
                                            hasEvaluation,
                                            audioClip,
                                            onClip,
                                            playerBlurb,
                                            speakerStore,
                                            disableClip,
                                        } satisfies TranscriptionRowData
                                    }
                                    itemKey={(index, data) =>
                                        data.rows[index]?.key ?? index
                                    }
                                    itemSize={getSize}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {TranscriptionRow}
                                </List>
                            </div>
                        )}
                    </Observer>
                )}
            </AutoSizer>
        );
    },
);
